// A staff-run station's countdown screen (ADR-0033; spec/13 §3.3, spec/09 §3).
//
//   GET    /v1/organizations/{o}/venues/{v}/stations/{s}/display   is a link live, and when was it seen    stations:manage
//   POST   /v1/organizations/{o}/venues/{v}/stations/{s}/display   link (or rotate) — the token, once      stations:manage
//   DELETE /v1/organizations/{o}/venues/{v}/stations/{s}/display   revoke                                  stations:manage
//   GET    /v1/public/displays/{token}                             what the screen shows                   unauthenticated
//   POST   /v1/public/displays/{token}/realtime-token              a display-scoped realtime token         unauthenticated
//
// **The token is the credential, and it is shown once.** It is 128 random bits (22 base64url characters); the api
// keeps only its SHA-256. Posting again rotates it — the old link stops — and the console shows the new one as a QR
// and the short URL `{CONSOLE_URL}/display/{token}` for the tablet or TV to open. Nothing about the token is read back:
// to show a QR again, rotate.
//
// **The public read is a separate projection**, not a stripped station or session: the station's number, name and
// kind, the venue's name and logo, and — while a session runs — the player's first name or gamer tag (never a surname,
// a phone number or an email), the time left, the grace length and end, and the title staff picked. It names no
// organisation, venue, station or session id. A revoked, rotated or unknown token is the same `404`.
//
// **Live updates** ride a display-scoped realtime principal: `kind: display`, whose only channel is
// `display:{stationId}`. Its `display.changed` frames carry nothing but "re-read", so the REST read — which checks the
// link — is the only source of the picture, and a socket that outlives a revoke learns nothing.
import { createRoute } from '@hono/zod-openapi';
import { z } from "../zod.js";
import { OrganizationIdSchema, StationDisplayIdSchema, StationIdSchema, VenueIdSchema } from "../ids/schema.js";
import { IsoDateTimeSchema } from "../primitives/scalars.js";
import { STATION_DISPLAY_TOKEN_PATTERN } from "../primitives/staff-run.js";
import { commonProblemResponses, JSON_CONTENT_TYPE, problemResponse } from "../openapi/responses.js";
import { STATIONS_MANAGE } from "../permissions/index.js";
import { PERMISSION_EXTENSION } from "./audit.js";
import { RealtimeTokenSchema } from "./realtime.js";
import { StationKindSchema } from "./stations.js";
// ---------------------------------------------------------------------------------------------
// The link, as staff manage it
// ---------------------------------------------------------------------------------------------
export const StationDisplayLinkSchema = z
    .object({
    id: StationDisplayIdSchema,
    stationId: StationIdSchema,
    createdAt: IsoDateTimeSchema,
    /** When a screen last read it, or `null` before one has. */
    lastSeenAt: IsoDateTimeSchema.nullable(),
})
    .openapi('StationDisplayLink', { description: 'A staff-run station’s live countdown-screen link (ADR-0033). Never the token.' });
export const StationDisplayStatusSchema = z
    .object({
    /** The live link, or `null` when the station has none (never linked, or revoked). */
    link: StationDisplayLinkSchema.nullable(),
})
    .openapi('StationDisplayStatus', { description: 'Whether a staff-run station has a live countdown-screen link.' });
export const StationDisplayTokenSchema = z
    .string()
    .regex(STATION_DISPLAY_TOKEN_PATTERN, 'Expected a display token: 22 base64url characters')
    .openapi('StationDisplayToken', { description: 'A countdown-screen token: 128 random bits, base64url.' });
export const CreatedStationDisplaySchema = z
    .object({
    link: StationDisplayLinkSchema,
    /** The token — shown once, here, and never again. */
    token: StationDisplayTokenSchema,
    /** Where the screen opens it: `{CONSOLE_URL}/display/{token}`. The console draws it as a QR. */
    url: z.string().url(),
})
    .openapi('CreatedStationDisplay', { description: 'A new (or rotated) countdown-screen link and its one-time token.' });
// ---------------------------------------------------------------------------------------------
// What the screen shows
// ---------------------------------------------------------------------------------------------
export const STATION_DISPLAY_SESSION_STATUSES = ['active', 'locked', 'grace'];
export const StationDisplaySessionStatusSchema = z.enum(STATION_DISPLAY_SESSION_STATUSES).openapi('StationDisplaySessionStatus', {
    description: 'Playing (`active`), on hold (`locked`), or out of funded time and in grace (`grace`).',
});
export const StationDisplaySessionSchema = z
    .object({
    status: StationDisplaySessionStatusSchema,
    /** The player's first name or gamer tag — nothing more (ADR-0033). `null` when the account carries neither. */
    playerLabel: z.string().min(1).max(60).nullable(),
    /** Seconds of funded time left at `serverTime`. 0 in grace. */
    timeLeftSeconds: z.int().min(0),
    /** When the funded time runs out, or `null` when nothing funds it. */
    fundedEndAt: IsoDateTimeSchema.nullable(),
    /** The grace the session gets once its time runs out, in seconds; 0 for none. */
    graceSeconds: z.int().min(0),
    /** When grace ends — present only in grace. */
    graceEndsAt: IsoDateTimeSchema.optional(),
    /** The title staff picked at the start, or `null` ("console play"). */
    title: z.string().min(1).max(200).nullable(),
    /**
     * `true` for an **Open** console session (ADR-0034): no end to count down to — the screen counts the time played up
     * from `startedAt` instead. Absent for a timed session.
     */
    open: z.boolean().optional(),
    /** When an Open session started, for the time played; present only with `open`. */
    startedAt: IsoDateTimeSchema.optional(),
})
    .openapi('StationDisplaySession', { description: 'The session on a countdown screen: a first name, the clock, the grace.' });
export const StationDisplayStationSchema = z
    .object({ number: z.int().min(1), name: z.string().nullable(), kind: StationKindSchema })
    .openapi('StationDisplayStation', { description: 'The station a countdown screen is for: its number, name and kind.' });
export const StationDisplayVenueSchema = z
    .object({
    name: z.string(),
    /** The venue's logo (else the organisation's), public media; `null`: show the name as a wordmark. */
    logoUrl: z.string().nullable(),
})
    .openapi('StationDisplayVenue', { description: 'The venue’s name and logo, for a countdown screen’s branding.' });
export const StationDisplayViewSchema = z
    .object({
    station: StationDisplayStationSchema,
    venue: StationDisplayVenueSchema,
    /** The session running now, or `null` when the station is free. */
    session: StationDisplaySessionSchema.nullable(),
    /** The player app, for the "Add time or order food on your phone" QR. */
    playerAppUrl: z.string(),
    /** The api's clock: the screen counts down against it. */
    serverTime: IsoDateTimeSchema,
})
    .openapi('StationDisplayView', { description: 'What a staff-run station’s countdown screen shows (ADR-0033). No PII beyond a first name.' });
// ---------------------------------------------------------------------------------------------
// The routes
// ---------------------------------------------------------------------------------------------
const StationDisplayParamsSchema = z
    .object({ organizationId: OrganizationIdSchema, venueId: VenueIdSchema, stationId: StationIdSchema })
    .openapi('StationDisplayParams');
const PublicDisplayParamsSchema = z.object({ token: z.string().min(1).max(64) }).openapi('PublicDisplayParams');
const forbidden = problemResponse('Your role does not allow this.', 'forbidden', 'second-factor-required');
const stationNotFound = problemResponse('No such station at this venue.', 'not-found');
const displayNotFound = problemResponse('No live countdown screen has that token: it was revoked, rotated, or never existed.', 'not-found');
export const getStationDisplayRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/stations/{stationId}/display',
    operationId: 'getStationDisplay',
    tags: ['stations'],
    summary: 'Whether a staff-run station has a live countdown screen',
    description: 'The live link and when a screen last read it — never the token. Requires `stations:manage`.',
    [PERMISSION_EXTENSION]: STATIONS_MANAGE,
    request: { params: StationDisplayParamsSchema },
    responses: {
        200: { description: 'The link, or none.', content: { [JSON_CONTENT_TYPE]: { schema: StationDisplayStatusSchema } } },
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: stationNotFound,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const createStationDisplayRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/stations/{stationId}/display',
    operationId: 'createStationDisplay',
    tags: ['stations'],
    summary: 'Link (or rotate) a staff-run station’s countdown screen',
    description: 'Creates the station’s countdown-screen link and returns its token once, with the URL to open. A link already ' +
        'live is revoked first (rotation). A managed station (a PC) is `409 conflict`: its own screen shows the time. ' +
        'Requires `stations:manage`. Audited, never the token.',
    [PERMISSION_EXTENSION]: STATIONS_MANAGE,
    request: { params: StationDisplayParamsSchema },
    responses: {
        201: { description: 'The link and its one-time token.', content: { [JSON_CONTENT_TYPE]: { schema: CreatedStationDisplaySchema } } },
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: stationNotFound,
        409: problemResponse('The station is managed: a PC shows its own time.', 'conflict'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const revokeStationDisplayRoute = createRoute({
    method: 'delete',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/stations/{stationId}/display',
    operationId: 'revokeStationDisplay',
    tags: ['stations'],
    summary: 'Revoke a staff-run station’s countdown screen',
    description: 'The screen’s next read is `404` and it says it was unlinked. Idempotent. Requires `stations:manage`. Audited.',
    [PERMISSION_EXTENSION]: STATIONS_MANAGE,
    request: { params: StationDisplayParamsSchema },
    responses: {
        204: { description: 'Revoked (or there was nothing to revoke).' },
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: stationNotFound,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const getPublicDisplayRoute = createRoute({
    method: 'get',
    path: '/v1/public/displays/{token}',
    operationId: 'getPublicDisplay',
    tags: ['public'],
    summary: 'What a countdown screen shows',
    description: 'Unauthenticated: the token is the credential. The station, the venue’s name and logo and the running session’s ' +
        'first name, clock and grace — nothing more. A revoked, rotated or unknown token is `404`. Not cached.',
    request: { params: PublicDisplayParamsSchema },
    responses: {
        200: { description: 'What the screen shows now.', content: { [JSON_CONTENT_TYPE]: { schema: StationDisplayViewSchema } } },
        400: commonProblemResponses[400],
        404: displayNotFound,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const createPublicDisplayRealtimeTokenRoute = createRoute({
    method: 'post',
    path: '/v1/public/displays/{token}/realtime-token',
    operationId: 'createPublicDisplayRealtimeToken',
    tags: ['public'],
    summary: 'A realtime token for a countdown screen',
    description: 'Unauthenticated: the token is the credential. A five-minute `kind: display` token whose only channel is ' +
        '`display:{stationId}` (spec/09 §3); refreshed through here, so a revoked link stops getting one. `404` as the read.',
    request: { params: PublicDisplayParamsSchema },
    responses: {
        200: { description: 'The realtime token.', content: { [JSON_CONTENT_TYPE]: { schema: RealtimeTokenSchema } } },
        400: commonProblemResponses[400],
        404: displayNotFound,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const STATION_DISPLAY_ROUTES = [
    getStationDisplayRoute,
    createStationDisplayRoute,
    revokeStationDisplayRoute,
    getPublicDisplayRoute,
    createPublicDisplayRealtimeTokenRoute,
];
//# sourceMappingURL=station-displays.js.map